import { format } from 'date-fns';
import { Tooltip } from './Tooltip';
import type { CampaignAxisEntry } from '../../types';

/**
 * One dot per campaign of a mailing list, oldest first, saying whether that member's **venue**
 * answered it (docs/CAMPAIGNS.md).
 *
 * The verdict is the venue's, not the contact's: a reply from any contact of the venue counts,
 * which is the whole point of folding the stored flag up. Read per email, a campaign that wrote
 * to two colleagues leaves the answer on the later mail alone, and the other row would deny a
 * reply the venue did send (docs/ANSWERS.md).
 */

export const MEMBER_ANSWERED = 'a';
export const MEMBER_MAILED_AGAIN = 'w';
export const MEMBER_NO_ANSWER = 'n';
export const MEMBER_NOT_MAILED = '-';

const DOTS: Record<string, { className: string; verdict: string }> = {
  [MEMBER_ANSWERED]: {
    className: 'bg-emerald-400',
    verdict: 'Answered — someone at this venue replied to it',
  },
  [MEMBER_MAILED_AGAIN]: {
    className: 'bg-amber-500/80',
    verdict: 'No answer — this venue was written to again afterwards, so a reply would belong to the newer mail',
  },
  [MEMBER_NO_ANSWER]: {
    className: 'border border-dark-text-muted',
    verdict: 'No answer',
  },
  [MEMBER_NOT_MAILED]: {
    className: 'bg-dark-border',
    verdict: 'Not sent to this member',
  },
};

/** Spelled out under the column header, so the dots need no legend of their own. */
export const ANSWER_STRIP_HINT =
  'One dot per campaign, oldest first: did the venue answer it? A reply from any contact of the ' +
  'venue counts, so colleagues read alike. Filled green = answered · amber = no answer, but the ' +
  'venue was written to again afterwards · hollow = no answer · grey = not sent to this member.';

interface MemberAnswerStripProps {
  /** One character per campaign; a shorter or missing strip reads as "not mailed". */
  strip: string;
  campaigns: CampaignAxisEntry[];
}

export function MemberAnswerStrip({ strip, campaigns }: MemberAnswerStripProps) {
  if (campaigns.length === 0) return <span className="text-dark-text-muted">—</span>;
  return (
    <div className="flex items-center gap-1">
      {campaigns.map((campaign, index) => {
        const state = strip[index] ?? MEMBER_NOT_MAILED;
        const dot = DOTS[state] ?? DOTS[MEMBER_NOT_MAILED];
        const size = state === MEMBER_NOT_MAILED ? 'w-1.5 h-1.5' : 'w-2.5 h-2.5';
        return (
          <Tooltip
            key={campaign.id}
            content={
              <span className="block max-w-xs">
                <span className="font-medium">{campaign.name}</span>
                <span className="block text-dark-text-secondary">
                  {format(new Date(campaign.at), 'dd MMM yyyy')} · {dot.verdict}
                </span>
              </span>
            }
          >
            <span className={`block rounded-full ${size} ${dot.className}`} />
          </Tooltip>
        );
      })}
    </div>
  );
}
